import React from 'react';
import { useCookies } from 'next-client-cookies';
import AlertBanner from 'components/commercetools-ui/molecules/alert-banner';
import { classifyCartKind, M2HText, P2RKind, P2RText } from 'helpers/constants/cart';
import { formattedPickupDate } from 'helpers/estimatedPickupDatehelper';
import { isProductAvailableInStore } from 'helpers/utils/util';
import { useRouter } from 'i18n/routing';
import { getLocalizationInfo } from 'project.config';
import { useAccount } from 'frontastic/hooks';
import { COLON, COMMA, DASH, STARMARK } from './constants';
import * as ProductStrip from './ProductStrip';
import { Cart } from './types';
import { getFulfillmentFromShippingKey } from './utils';
import { TruckIcon, StoreIcon } from '../../../icons/index';
import { getTotalItemsOfCarts } from '../cart/helpers';
import { HeaderContentProps } from '../header/types/gmsb-header';
import MaximumCartValueAlert from '../maximum-cart-value-alert';
import { formatMoney } from '../product/product-details/helpers/price-utils';

export type SuperCartCardProps = {
  cart: Cart;
  contentConfig: HeaderContentProps;
  pickupStore?: any;
  idx?: number;
};

export const SuperCartCard: React.FC<SuperCartCardProps> = ({ cart, contentConfig, pickupStore, idx }) => {
  const validLineItems = cart?.lineItems?.filter((item) => !item?.custom?.fields?.parentLineItem) ?? [];
  const totalItems = getTotalItemsOfCarts([cart]);

  const {
    minicartButtonText,
    minicartEarliestpickupTimetext,
    minicartPickupAtText,
    minicartShipToText,
    minicartShipingText,
    minicartTaxincludedText,
    shipsinMinDays,
    shipsinMaxDays,
    shipsinDaysText,
    ariaLabelReviewCheckoutBtn,
    maxCartAlert,
  } = contentConfig;

  const router = useRouter();
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { loggedIn } = useAccount();

  const distributionKey = cart?.lineItems?.[0]?.distributionChannel?.key ?? '';

  const channelKey = distributionKey?.split('-')?.[1]?.toLowerCase()?.trim();

  const channelName = channelKey && channelKey.charAt(0).toUpperCase() + channelKey.slice(1);

  const isCvs = channelName?.toLowerCase() === 'cvs';

  const meta = getFulfillmentFromShippingKey(distributionKey);

  const requiredLocale = getLocalizationInfo(locale);

  const formattedEstimate = formattedPickupDate(
    pickupStore?.pickupDateTime as string,
    pickupStore?.pickupTimeZone as string,
    requiredLocale?.locale,
  );

  const priceText = formatMoney(
    {
      type: 'centPrecision',
      centAmount: cart?.subTotalAmount?.centAmount,
      currencyCode: cart?.subTotalAmount?.currencyCode,
      fractionDigits: cart?.subTotalAmount?.fractionDigits,
    },
    locale,
  );

  const isOutOfStockProduct = cart?.lineItems?.some(
    (item) => !isProductAvailableInStore(pickupStore?.availableProductCodes, item?.variant?.attributes),
  );

  const isDataReady = pickupStore !== undefined && typeof pickupStore?.isServiceAvailable === 'boolean';

  const cartKind = classifyCartKind(distributionKey);
  const cartType = cartKind === P2RKind ? P2RText : M2HText;

  const shouldShowTaxText = Boolean(meta.isTaxText);

  const shouldShowOutOfStockAlert =
    isDataReady && meta.isP2R && ((pickupStore && !pickupStore.isServiceAvailable) || isOutOfStockProduct);

  const handleReviewCheckout = () => {
    const url =
      cartType === P2RText ? `/cart/${cart.cartId}?hl=${locale}` : `/cart/${cart.cartId}?step=cart&hl=${locale}`;

    router.push(url);
  };

  const renderCardTitle = () => {
    if (!meta.isP2R) {
      return minicartShipToText;
    }

    return (
      <>
        <span data-ffid={`lbl-minicart-pickupat-text-${idx}`}>{minicartPickupAtText} </span>
        <span data-ffid={`lbl-minicart-channel-name-text-${idx}`} className={isCvs ? 'uppercase' : ''}>
          {channelName}
        </span>
      </>
    );
  };

  const renderSubtitle = () => {
    if (meta.isP2R) {
      return (
        <span data-ffid={`lbl-p2r-address-${idx}`}>
          {formattedEstimate ? (
            <>
              {isCvs ? channelName?.toUpperCase() : channelName} {cart?.shippingAddress?.city}
              {COMMA} {minicartEarliestpickupTimetext}
              {COLON} {formattedEstimate}
              {` (`}
              {totalItems}
              {`)`}
            </>
          ) : (
            <span className="block h-20 animate-pulse rounded-md bg-gray-300" />
          )}
        </span>
      );
    }

    if (!minicartShipingText) {
      return null;
    }

    return (
      <>
        <span className="text-[#333]" data-ffid={`lbl-m2h-address-${idx}`}>
          <span data-ffid={`lbl-m2h-shipping-text-${idx}`}>{minicartShipingText}</span>{' '}
          <span data-ffid={`lbl-m2h-ships-in-min-days-text-${idx}`}>{shipsinMinDays}</span>
          {DASH}
          <span data-ffid={`lbl-m2h-ships-in-max-days-text-${idx}`}>{shipsinMaxDays}</span>{' '}
          <span data-ffid={`lbl-m2h-ships-in-days-text-${idx}`}>{shipsinDaysText}</span>
        </span>
        {` (`}
        {totalItems}
        {`)`}
      </>
    );
  };

  if (!cart?.lineItems?.length) {
    return null;
  }

  return (
    <>
      <section
        className={`rounded-xl border border-[#EBEBEB] bg-white p-16 ${shouldShowTaxText ? 'mb-0' : 'mb-20'} shadow-sm`}
        data-testid="super-cart-card"
        data-ffid={`ctr-minicart-single-cart-${cartType}`}
      >
        <div className="flex items-start justify-between pb-16">
          <div className="flex items-center gap-6">
            {meta.icon === 'truck' ? (
              <TruckIcon data-ffid={`img-minicart-m2h-icon-${idx}`} className="size-25 text-gray-700" aria-hidden />
            ) : (
              <StoreIcon data-ffid={`img-minicart-p2r-icon-${idx}`} className="size-25 text-gray-700" aria-hidden />
            )}

            <h3
              data-ffid={`lbl-minicart-${meta.isP2R ? 'p2r-text' : 'm2h-text'}-icon-${idx}`}
              className="text-14 font-bold text-[#333]"
              data-testid="card-title"
            >
              {renderCardTitle()} {'('}
              {totalItems}
              {')'}
            </h3>
          </div>

          <div className="text-14 font-bold leading-6 text-[#333]" data-testid="card-total">
            <span data-ffid={`lbl-minicart-total-line-item-price-${idx}`}>{priceText}</span>

            {meta.showPriceAsterisk && (
              <sup data-ffid={`lbl-minicart-p2r-asterisk-price-${idx}`} className="ml-0.5 text-gray-500">
                {STARMARK}
              </sup>
            )}
          </div>
        </div>

        <div className="relative mt-0 border-b border-neutral-400 pb-16">
          <button
            type="button"
            onClick={handleReviewCheckout}
            className="relative w-full rounded-full bg-[#018463] px-4 py-8 text-14 font-normal text-white transition-all ease-in-out hover:bg-[#018463] focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
            aria-label={ariaLabelReviewCheckoutBtn}
            data-testid="review-checkout-btn"
            data-ffid={`btn-review-and-checkout-${cartType}`}
            disabled={!loggedIn}
          >
            {minicartButtonText}
          </button>
        </div>

        <div className="py-16">
          <p className="mb-16 text-12 text-gray-700" data-testid="card-subtitle">
            {renderSubtitle()}
          </p>

          <ProductStrip.ProductStrip idx={idx} items={validLineItems} />
        </div>

        {maxCartAlert && cart.sum && <MaximumCartValueAlert maxCartValueText={maxCartAlert} cartSum={cart.sum} />}

        {shouldShowOutOfStockAlert && (
          <AlertBanner className="mb-[0.8rem]">
            <p data-ffid={`lbl-minicart-out-of-stock-text-${idx}`}>{contentConfig.outOfStockLabel as string}</p>
          </AlertBanner>
        )}
      </section>

      {shouldShowTaxText && (
        <div data-ffid={`lbl-minicart-tax-text-${idx}`} className="pb-20 pt-4 text-14">
          {minicartTaxincludedText}
        </div>
      )}
    </>
  );
};

/* export const SuperCartCard: React.FC<SuperCartCardProps> = ({ cart, contentConfig, pickupStore, idx }) => {
  const validLineItems = cart?.lineItems?.filter((item) => !item?.custom?.fields?.parentLineItem);
  const totalItems = validLineItems?.reduce((acc, curr) => acc + curr.count, 0) ?? 0;
  const {
    minicartButtonText,
    minicartEarliestpickupTimetext,
    minicartPickupAtText,
    minicartShipToText,
    minicartShipingText,
    minicartTaxincludedText,
    shipsinMinDays,
    shipsinMaxDays,
    shipsinDaysText,
    ariaLabelReviewCheckoutBtn,
  } = contentConfig;

  const channelKey = cart?.lineItems?.[0]?.distributionChannel?.key?.split('-')?.[1]?.toLowerCase()?.trim();
  const channelName = channelKey && channelKey?.charAt?.(0)?.toUpperCase() + channelKey?.slice?.(1);
  const router = useRouter();
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;

  const meta = getFulfillmentFromShippingKey(cart?.lineItems?.[0]?.distributionChannel?.key as string);
  const requiredLocale = getLocalizationInfo(locale);

  const formattedEstimate = formattedPickupDate(
    pickupStore?.pickupDateTime as string,
    pickupStore?.pickupTimeZone as string,
    requiredLocale?.locale,
  );
  const priceText = formatMoney(
    {
      type: 'centPrecision',
      centAmount: cart.sum.centAmount,
      currencyCode: cart.sum.currencyCode,
      fractionDigits: cart.sum.fractionDigits,
    },
    locale,
  );
  const maxCartAlert = contentConfig.maxCartAlert;

  const isOutOfStockProduct = cart?.lineItems.some((item) => {
    return !isProductAvailableInStore(pickupStore?.availableProductCodes, item?.variant?.attributes);
  });

  const isDataReady = pickupStore !== undefined && typeof pickupStore?.isServiceAvailable === 'boolean';

  const cartKind = classifyCartKind(cart?.lineItems?.[0]?.distributionChannel?.key);

  const cartType = cartKind === P2RKind ? P2RText : M2HText;

  const { loggedIn } = useAccount();

  return (
    <>
      {cart?.lineItems?.length > 0 && (
        <>
          <section
            className={`rounded-xl border border-[#EBEBEB] bg-white p-16 ${meta.isTaxText ? 'mb-0' : 'mb-20'} shadow-sm`}
            data-testid="super-cart-card"
            data-ffid={`ctr-minicart-single-cart-${cartType}`}
          >
            <div className="flex items-start justify-between pb-16">
              <div className="flex items-center gap-6">
                {meta.icon === 'truck' ? (
                  <TruckIcon
                    data-ffid={`img-minicart-m2h-icon-${idx}`}
                    className="size-25 text-gray-700"
                    aria-hidden
                  />
                ) : (
                  <StoreIcon
                    data-ffid={`img-minicart-p2r-icon-${idx}`}
                    className="size-25 text-gray-700"
                    aria-hidden
                  />
                )}
                <h3
                  data-ffid={`lbl-minicart-${meta.isP2R ? 'p2r-text' : 'm2h-text'}-icon-${idx}`}
                  className="text-14 font-bold text-[#333]"
                  data-testid="card-title"
                >
                  {meta.isP2R ? (
                    <>
                      <span data-ffid={`lbl-minicart-pickupat-text-${idx}`}>{minicartPickupAtText} </span>
                      <span
                        data-ffid={`lbl-minicart-channel-name-text-${idx}`}
                        className={channelName?.toLowerCase() == 'cvs' ? 'uppercase' : ''}
                      >
                        {channelName}
                      </span>
                    </>
                  ) : (
                    minicartShipToText
                  )}{' '}
                  {`(`}
                  {totalItems}
                  {`)`}
                </h3>
              </div>

              <div className="text-14 font-bold leading-6 text-[#333]" data-testid="card-total">
                <span data-ffid={`lbl-minicart-total-line-item-price-${idx}`}>{priceText}</span>
                {meta.showPriceAsterisk ? (
                  <sup data-ffid={`lbl-minicart-p2r-asterisk-price-${idx}`} className="ml-0.5 text-gray-500">
                    {STARMARK}
                  </sup>
                ) : null}
              </div>
            </div>

            <div className="relative mt-0 border-b border-neutral-400 pb-16">
              <button
                type="button"
                onClick={() => {
                  const isP2R = cartType === 'p2r';
                  router.push(
                    isP2R ? `/cart/${cart.cartId}?hl=${locale}` : `/cart/${cart.cartId}?step=cart&hl=${locale}`,
                  );
                }}
                className="relative w-full rounded-full bg-green-800 px-4 py-8 text-14 font-normal text-white transition-all ease-in-out hover:bg-[#349D82] focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                aria-label={ariaLabelReviewCheckoutBtn}
                data-testid="review-checkout-btn"
                data-ffid={`btn-review-and-checkout-${cartType}`}
                disabled={!loggedIn}
              >
                {minicartButtonText}
              </button>
            </div>
            <div className="py-16">
              <p className="mb-16 text-12 text-gray-700" data-testid="card-subtitle">
                {meta.isP2R ? (
                  <span data-ffid={`lbl-p2r-address-${idx}`}>
                    {formattedEstimate ? (
                      <>
                        {channelName?.toLowerCase() == 'cvs' ? channelName.toUpperCase() : channelName}{' '}
                        {cart?.shippingAddress?.city}
                        {COMMA} {minicartEarliestpickupTimetext}
                        {COLON} {formattedEstimate}
                        {` (`}
                        {totalItems}
                        {`)`}
                      </>
                    ) : (
                      <span className="block h-20 animate-pulse rounded-md bg-gray-300" />
                    )}
                  </span>
                ) : (
                  minicartShipingText && (
                    <>
                      <span className="text-[#333]" data-ffid={`lbl-m2h-address-${idx}`}>
                        <span data-ffid={`lbl-m2h-shipping-text-${idx}`}>{minicartShipingText}</span>{' '}
                        <span data-ffid={`lbl-m2h-ships-in-min-days-text-${idx}`}>{shipsinMinDays}</span>
                        {DASH}
                        <span data-ffid={`lbl-m2h-ships-in-max-days-text-${idx}`}>{shipsinMaxDays}</span>
                        <span data-ffid={`lbl-m2h-ships-in-days-text-${idx}`}>{shipsinDaysText}</span>
                      </span>
                      {` (`}
                      {totalItems}
                      {`)`}
                    </>
                  )
                )}
              </p>
              <ProductStrip.ProductStrip idx={idx} items={validLineItems} />
            </div>
            {maxCartAlert && cart.sum && <MaximumCartValueAlert maxCartValueText={maxCartAlert} cartSum={cart.sum} />}

            {isDataReady && meta.isP2R && ((pickupStore && !pickupStore.isServiceAvailable) || isOutOfStockProduct) && (
              <AlertBanner className="mb-[0.8rem]">
                <p data-ffid={`lbl-minicart-out-of-stock-text-${idx}`}>{contentConfig?.outOfStockLabel as string}</p>
              </AlertBanner>
            )}
          </section>
          {meta.isTaxText ? (
            <div
              data-ffid={`lbl-minicart-tax-text-${idx}`}
              className={`${meta.isTaxText ? 'pb-20 pt-4 text-14' : 'mb-0'}`}
            >
              {minicartTaxincludedText}
            </div>
          ) : (
            <div></div>
          )}
        </>
      )}
    </>
  );
}; */
